import React, { useCallback, useContext, useEffect, useState } from 'react';

import { t } from 'i18next';
import { Trans } from 'react-i18next';

import { ReactComponent as HappyBuddy } from '../../../../../../assets/icons/buddy-happy-open-eyes.svg';
import BdsComboBox from '../../../../../../shared/components/v3/BdsComboBox/BdsComboBox';
import BdsModal from '../../../../../../shared/components/v3/Modals/BdsModal/BdsModal';
import Toast from '../../../../../../shared/components/v3/Toast/Toast';
import {
  ADD_COLLABORATOR_FAILED_TOAST,
  ADD_COLLABORATOR_SUCCESS_TOAST
} from '../../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../../shared/context/company/companyContext';
import { PlansContext } from '../../../../../../shared/context/plans/plansContext';
import { ICollaboratorsModal } from '../../../../../../shared/interfaces/collaboratosModal';
import { IMemberToAddsData } from '../../../../../../shared/interfaces/responseInterface';
import { IToast } from '../../../../../../shared/interfaces/toast';
import Holding from '../../../../../../shared/services/api/holdings/holdings';
import { TRANSLATION_COLLABORATORS } from '../../../../../../shared/translations/collaborators';

const CollaboratorsAssignModal = ({ openModal, setOpenModal, emails, onFinally }: ICollaboratorsModal) => {
  const [selectedPlanId, setSelectedPlanId] = useState<string>();
  const [disabledButton, setDisabledButton] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [toastData, setToastData] = useState<IToast>();
  const [membersData, setMembersData] = useState<IMemberToAddsData[]>([]);
  const { companyData, holding } = useContext(CompanyContext);
  const { plans } = useContext(PlansContext);

  const handleAddCollaboratorToPlan = useCallback(
    (plan) => {
      const updatedMembersData = emails.map((email) => ({
        companyId: companyData.id,
        email,
        planId: plan
      }));
      setMembersData(updatedMembersData);
      setSelectedPlanId(plan);
      setIsExpanded(false);
    },
    [companyData, emails]
  );

  const handleOnCollapse = (value: boolean) => {
    setIsExpanded(value);
  };

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const subsEmails = async () => {
    setLoading(true);
    try {
      await Holding.assignMembers(holding.id, membersData);

      await onFinally(emails);

      setToastData(ADD_COLLABORATOR_SUCCESS_TOAST);
    } catch {
      setToastData(ADD_COLLABORATOR_FAILED_TOAST);
    } finally {
      setOpenToast(true);
      setLoading(false);
      handleOnCloseModal();
    }
  };

  const handleOnCloseModal = useCallback(() => {
    if (!loading) {
      setSelectedPlanId(undefined);
      setOpenModal(false);
    }
  }, [loading, setOpenModal]);

  useEffect(() => {
    if (plans?.length === 1) {
      const updatedMembersData = emails.map((email) => ({
        companyId: companyData.id,
        email,
        planId: plans === null ? '' : plans[0].id.toString()
      }));
      setMembersData(updatedMembersData);
    }
  }, [plans, companyData, emails]);

  useEffect(() => {
    if (selectedPlanId || isExpanded) {
      setDisabledButton(false);
    } else {
      setDisabledButton(true);
    }
  }, [selectedPlanId, isExpanded]);

  const renderModalToOnlyOnePlan = () => {
    return (
      <BdsModal
        open={openModal}
        icon={<HappyBuddy />}
        title={t(TRANSLATION_COLLABORATORS.modal.addCollaborators.title, { count: emails.length })}
        loading={loading}
        description={
          <Trans
            i18nKey={TRANSLATION_COLLABORATORS.modal.addCollaborators.description}
            values={{ user_email: emails.map((email) => email), count: emails.length }}
            components={{
              b: <b></b>
            }}
          />
        }
        onClose={handleOnCloseModal}
        primaryAction={{
          label: t(TRANSLATION_COLLABORATORS.modal.addCollaborators.button, { count: emails.length }),
          onClick: subsEmails
        }}
      />
    );
  };

  const renderModalToMoreThanOnePlan = () => {
    const primaryAction = isExpanded
      ? undefined
      : {
          disabled: disabledButton,
          label: t(TRANSLATION_COLLABORATORS.modal.addCollaborators.button, { count: emails.length }),
          onClick: subsEmails
        };
    return (
      <BdsModal
        open={openModal}
        icon={<HappyBuddy />}
        title={t(TRANSLATION_COLLABORATORS.modal.addCollaborators.titleSelectPlan, { count: emails.length })}
        description={
          <BdsComboBox
            items={
              plans?.map((plan) => ({
                title: plan.name,
                subtitle: [companyData.name, plan.price.toString(), plan.currency],
                value: plan.id.toString()
              })) || []
            }
            onChange={handleAddCollaboratorToPlan}
            placeHolder={t(TRANSLATION_COLLABORATORS.modal.addCollaborators.selectPlanPlaceHolder)}
            value={selectedPlanId}
            onCollapse={handleOnCollapse}
          />
        }
        loading={loading}
        primaryAction={primaryAction}
        onClose={handleOnCloseModal}
      />
    );
  };

  return (
    <>
      {(plans.length ?? 0) > 1 ? renderModalToMoreThanOnePlan() : renderModalToOnlyOnePlan()}
      <Toast
        title={t(toastData?.title || '', { count: emails.length })}
        description={t(toastData?.description || '', { count: emails.length })}
        open={openToast}
        button={toastData?.button}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
        icon={toastData?.icon}
      />
    </>
  );
};
export default CollaboratorsAssignModal;
